import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Search, ShoppingCart, Store, ShieldAlert } from "lucide-react";
import { myApi } from "@/my/api";

function fmtEur(value) {
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(Number(value) || 0);
}

export default function MyRevenda() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState({});  // {ID_Produto: 'adding'}

  useEffect(() => {
    (async () => {
      try {
        const data = await myApi.revendaProducts();
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        if (e?.status === 403) setError("Esta página é exclusiva para consultores.");
        else setError(e?.message || "Erro ao carregar produtos.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => (p.Descricao || "").toLowerCase().includes(q));
  }, [items, search]);

  async function handleAdd(p) {
    if (adding[p.ID_Produto]) return;
    setAdding((cur) => ({ ...cur, [p.ID_Produto]: "adding" }));
    try {
      await myApi.cartAdd(p.ID_Produto, 1, "soma");
      // UX request: ir directamente para o carrinho assim que o item é
      // adicionado com sucesso, em vez de mostrar o estado "Adicionado!" e
      // deixar o consultor confirmar manualmente.
      navigate("/my/carrinho");
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.message || "Erro ao adicionar ao carrinho.");
      setAdding((cur) => {
        const next = { ...cur };
        delete next[p.ID_Produto];
        return next;
      });
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="my-revenda-loading">
        <Loader2 className="animate-spin text-[#0F1829]" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center" data-testid="my-revenda-error">
        <ShieldAlert size={40} className="mx-auto text-[#B91C1C] mb-3" />
        <h2 className="text-xl font-semibold text-[#0F1829] mb-2">Não foi possível abrir esta página</h2>
        <p className="text-[#4B5563]">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="my-revenda-page">
      <header className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-[14px] bg-[#0F1829] text-white flex items-center justify-center">
            <Store size={20} strokeWidth={1.6} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
              Produtos Revenda
            </h1>
            <p className="text-sm text-[#4B5563]">
              Catálogo exclusivo para consultores. Adicione ao carrinho e finalize como uma compra normal.
            </p>
          </div>
        </div>
      </header>

      {/* Search */}
      <div className="mb-6 relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
        <input
          type="search"
          data-testid="my-revenda-search"
          placeholder="Pesquisar por descrição…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-3 rounded-[14px] border border-[#E6E1D6] bg-white focus:outline-none focus:border-[#0F1829] transition"
        />
      </div>

      {/* Counters */}
      <div className="text-sm text-[#6B7280] mb-3" data-testid="my-revenda-count">
        {filtered.length} {filtered.length === 1 ? "produto" : "produtos"}
        {search && items.length !== filtered.length && (
          <span className="ml-1">de {items.length} no total.</span>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="py-16 text-center text-[#9CA3AF]" data-testid="my-revenda-empty">
          Sem produtos a apresentar.
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="my-revenda-list">
          {filtered.map((p) => {
            const state = adding[p.ID_Produto];
            return (
              <li
                key={p.ID_Produto}
                className="bg-white border border-[#E6E1D6] rounded-[18px] p-5 flex flex-col gap-4"
                data-testid={`my-revenda-item-${p.ID_Produto}`}
              >
                <div className="flex-1">
                  <div className="text-xs text-[#9CA3AF] uppercase tracking-wide mb-1">#{p.ID_Produto}</div>
                  <h3 className="text-base font-medium text-[#0F1829] leading-snug" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {p.Descricao || "Sem descrição"}
                  </h3>
                </div>

                <div className="border-t border-[#F3F0E8] pt-3">
                  <div className="text-xs uppercase tracking-wide text-[#6B7280]">Valor</div>
                  <div className="text-xl font-semibold text-[#0F1829]" data-testid={`my-revenda-price-${p.ID_Produto}`}>
                    {fmtEur(p.PrecoClienteFinal)}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleAdd(p)}
                  disabled={!!state}
                  data-testid={`my-revenda-add-${p.ID_Produto}`}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-[14px] text-sm font-medium transition ${
                    state === "adding"
                      ? "bg-[#9CA3AF] text-white cursor-wait"
                      : "bg-[#0F1829] text-white hover:bg-[#1f2a4a]"
                  }`}
                >
                  {state === "adding" ? <Loader2 size={16} className="animate-spin" /> : <ShoppingCart size={16} />}
                  <span>
                    {state === "adding" ? "A adicionar…" : "Adicionar ao carrinho"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-10 text-center">
        <Link
          to="/my/carrinho"
          className="inline-flex items-center gap-2 text-sm text-[#0F1829] underline hover:no-underline"
          data-testid="my-revenda-goto-cart"
        >
          <ShoppingCart size={16} /> Ir para o carrinho
        </Link>
      </div>
    </div>
  );
}
